import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { FIELD_LABEL } from "@/features/clients/components/profile-form-shared";
import { SelectField } from "@/features/clients/components/record-form-fields";
import { transfersApi } from "@/features/worksheets/api";
import { CrudDialog } from "@/features/worksheets/components/crud-dialog";
import { MoneyField } from "@/features/worksheets/components/money-field";
import { centsToDollars, dollarsToCents } from "@/features/worksheets/money";
import type { AssetTransfer } from "@/features/worksheets/types";

const TRANSFER_TYPE_OPTIONS = [
  { value: "N", label: "Asset" },
  { value: "R", label: "Real estate" },
];

interface TransferFormValues {
  transferType: "N" | "R";
  description: string;
  amount: string;
  transferDate: string;
  toWhom: string;
}

const validationSchema = Yup.object({
  transferType: Yup.string().required("Type is required"),
  description: Yup.string().required("Description is required"),
});

function toInitialValues(record: AssetTransfer | null): TransferFormValues {
  return {
    transferType: record?.transferType ?? "N",
    description: record?.description ?? "",
    amount: record ? centsToDollars(record.amount) : "",
    transferDate: record?.transferDate ?? "",
    toWhom: record?.toWhom ?? "",
  };
}

function toPayload(v: TransferFormValues): Record<string, unknown> {
  return {
    transferType: v.transferType,
    description: v.description,
    amount: dollarsToCents(v.amount),
    transferDate: v.transferDate || null,
    toWhom: v.toWhom,
  };
}

export function TransferDialog({
  caseId,
  record,
  open,
  onOpenChange,
  onSaved,
}: {
  caseId: string;
  record: AssetTransfer | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}) {
  async function onSave(values: TransferFormValues) {
    const payload = toPayload(values);
    if (record) {
      await transfersApi.update(caseId, record.id, payload);
    } else {
      await transfersApi.create(caseId, payload);
    }
  }

  return (
    <CrudDialog
      open={open}
      onOpenChange={onOpenChange}
      editing={record !== null}
      noun="Transfer"
      initialValues={toInitialValues(record)}
      validationSchema={validationSchema}
      onSave={onSave}
      onSaved={onSaved}
    >
      <SelectField name="transferType" label="Type" options={TRANSFER_TYPE_OPTIONS} />
      <FormField
        name="description"
        label="Description"
        labelClassName={FIELD_LABEL}
        placeholder="What was transferred"
      />
      <div className="grid grid-cols-2 gap-4">
        <MoneyField name="amount" label="Amount" />
        <FormField
          name="transferDate"
          label="Transfer Date"
          labelClassName={FIELD_LABEL}
          type="date"
        />
      </div>
      <FormField name="toWhom" label="To Whom" labelClassName={FIELD_LABEL} />
    </CrudDialog>
  );
}
